import * as React from 'react';
import { useNavigationContext } from '@restworld/utils-common';
import { MouseEventHandler, useState, ReactNode } from 'react';
import { cn } from '@restworld/utils-common';
/**
 * A button component that is anchor element inside.
 *
 * @param onClick - The click event handler for the button.
 * @param children - The content to be displayed inside the button.
 * @param isDropdownItem - A boolean indicating whether the button is a dropdown item.
 * @param isActive - A boolean indicating whether the button is currently active.
 * @param href - The URL to navigate to when the button is clicked.
 * @param twStyle - The Tailwind CSS styles to apply to the button.
 * @param rest - The rest of the props like ref, target, as to be passed to the button.
 * @example
 * <NavigationButton
      isActive={active}
      isDropdownItem
      onClick={(e) => {
        if (v.key === 'open-intercom') {
          e.preventDefault();
          showIntercom();
        }
      }}
      {...{
        href: v.url,
        target: v.openOnNewTab ? '_blank' : '_self',
        rel: v.openOnNewTab ? 'noopener noreferrer' : '',
      }}
    >
      {v.label}
    </NavigationButton>
 */
export const NavigationButton = ({
  onClick,
  children,
  isDropdownItem,
  isActive,
  href,
  twStyle,
  testId,
  ...rest
}: {
  onClick?: MouseEventHandler<HTMLAnchorElement> | undefined;
  children: ReactNode;
  isDropdownItem?: boolean;
  isActive?: boolean;
  href?: string;
  twStyle?: string;
  noPadding?: boolean;
  testId?: string;
}) => {
  const [isHover, setIsHover] = useState(false);
  const { linkComponent: Link } = useNavigationContext();

  return (
    <div onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
      <StyledNavButton
        twStyle={twStyle}
        isDropdownItem={isDropdownItem}
        onClick={onClick}
        isActive={isActive}
        href={href}
        isHover={isHover}
        as={!!href && !!Link ? (Link as any) : undefined}
        data-cy={testId}
        {...rest}
      >
        {children}
      </StyledNavButton>
      <HoverLine {...{ isHover, isDropdownItem }} />
    </div>
  );
};

const HoverLine = ({
  isDropdownItem,
  isHover,
  className,
  ...props
}: { isDropdownItem?: boolean; isHover?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <span
    className={cn(
      'block border-b-[1.5px] border-transparent w-2/3 mx-auto transition-all duration-200 ease-linear',
      isHover && !isDropdownItem && 'border-primary',
      className
    )}
    {...props}
  />
);

const StyledNavButton = ({ isDropdownItem, isActive, isHover, noPadding, twStyle, className, ...props }: any) => (
  <a
    className={cn(
      'block px-4 py-2 text-black text-base cursor-pointer transition-[color] duration-200 ease-linear',
      'lg:text-base lg:w-fit',
      isDropdownItem
        ? [
            'flex flex-row justify-start gap-2 w-full px-4 py-3 text-base leading-5',
            isActive ? 'bg-primary-50 text-gray-700 lg:w-full w-full rounded-lg' : '',
          ]
        : '',
      isHover ? 'text-primary' : '',
      noPadding ? 'px-0 py-9' : '',
      twStyle,
      className
    )}
    {...props}
  />
);
